// ============================================================================
// GROUPING WRAPPER - HOOK
// ============================================================================

import { BaseEntity } from "@/components/shared/UniversalCRMTable/types/shared";
import { useOptionalUniversalEntityPageCallbacksContext } from "../UniversalEntityPage/contexts/UniversalEntityPageCallbacksContext";
import { useOptionalUniversalEntityPageDataContext } from "../UniversalEntityPage/contexts/UniversalEntityPageDataContext";
import { useOptionalUniversalEntityPageHandlersContext } from "../UniversalEntityPage/contexts/UniversalEntityPageHandlersContext";
import { useOptionalUniversalEntityPageStaticContext } from "../UniversalEntityPage/contexts/UniversalEntityPageStaticContext";
import { useOptionalUniversalEntityPageUIContext } from "../UniversalEntityPage/contexts/UniversalEntityPageUIContext";
import type { GroupingWrapperProps, GroupingWrapperData } from "./types";

/**
 * Hook to get GroupingWrapper data from context or props
 * Prefers context when available (inside UniversalEntityPage)
 * Falls back to props for backward compatibility
 *
 * Reads the four narrow contexts rather than the god context, so the wrapper is
 * only invalidated by the slices it actually renders from. All five reads are
 * unconditional (hook order is stable) and non-throwing — outside an entity
 * page every one returns `null` and the props path is used.
 */
export function useGroupingWrapperData<T extends BaseEntity>(
  props: GroupingWrapperProps<T>
): GroupingWrapperData<T> {
  const staticContext = useOptionalUniversalEntityPageStaticContext<T>();
  const dataContext = useOptionalUniversalEntityPageDataContext<T>();
  const uiContext = useOptionalUniversalEntityPageUIContext<T>();
  const handlers = useOptionalUniversalEntityPageHandlersContext<T>();
  const callbacks = useOptionalUniversalEntityPageCallbacksContext<T>();

  if (!staticContext || !dataContext || !uiContext || !handlers || !callbacks) {
    // Not inside an entity page — use the props the caller passed.
    return props;
  }

  const { dataState } = dataContext;
  const { filterState } = uiContext;

  return {
    data: dataState.processedData,
    config: staticContext.pageConfig.config,
    searchTerm: filterState.searchTerm,
    setSearchTerm: filterState.setSearchTerm,
    filters: filterState.tableFilters,
    setFilters: filterState.setTableFilters,
    onExport: callbacks.handleGroupingExport,
    onEdit: handlers.handleEditItem,
    onView: handlers.handleViewItem,
    onOpenDetailModal: handlers.handleOpenDetailModal,
    // Pagination props for card view
    hasNextPage: dataState.hasNextPage,
    isFetchingNextPage: dataState.isFetchingNextPage,
    onLoadMore: dataState.loadMore,
    // Entity hooks for actions (cards and table rows)
    hooks: staticContext.pageConfig.hooks,
  };
}
